import { useQuery } from "@tanstack/react-query";
import { unauthedRequest } from "utils/api/requests";

/**
 * One session (`GET /api/v2/gifs/:id`).
 *
 * rails never password-gates this read (openapi.snapshot.yaml; rails #1384): a
 * shared photo opens with a valid, invalid or absent `k`, even in a
 * password-protected gallery. Only the gallery lists are gated. For
 * compatibility with a rails build that still answers
 * `401 { is_protected: true }` here, the request also forwards:
 *   - `k`: the per-photo key a share text/email links with (`?k=` on the page
 *     URL).
 *   - `unlock_token`: the channel unlock the gallery password gate stores
 *     (`getUnlockToken(slug)`).
 *
 * Both are in the query key, so storing a fresh unlock token (or arriving with
 * a different `k`) issues a new request instead of serving a cached 401.
 */
export const useGetSingleGif = (id, { k, unlockToken } = {}) => {
  // URL-encoded: both are signed tokens whose base64 can carry `+`, `/` and
  // `=`, and a raw `+` in a query string reads back as a space.
  const params = new URLSearchParams();
  if (k) params.set("k", k);
  if (unlockToken) params.set("unlock_token", unlockToken);
  const query = params.toString();

  return useQuery({
    queryFn: () =>
      unauthedRequest(`v2/gifs/${id}${query ? `?${query}` : ""}`, {
        method: "GET",
      }),
    queryKey: ["gif", id, k || null, unlockToken || null],
    retry: 0,
    staleTime: Infinity,
    enabled: !!id,
    select: (data) => data,
  });
};
